import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import PremiumDashboard from './Premiumdashboard';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { actions } from 'coupon-common/src/modules/dashboards';
import { actions as followerActions } from 'coupon-common/src/modules/followers/duck';
import { actions as usedCouponsListActions } from 'coupon-common/src/modules/usedCoupons/list';
import { actions as couponsActions } from 'coupon-common/src/modules/coupons';

/**
 * INTEGRATION GUIDE
 * ================
 *
 * This file shows how to connect the PremiumDashboard component to your existing
 * Redux state and API structure from Dashboard.js
 *
 * SETUP:
 * 1. Replace your old dashboard import with PremiumDashboardContainer
 * 2. All API data fetching is handled automatically via Redux
 * 3. The component uses your existing action dispatchers
 */

// Wrapper component that connects Redux state to PremiumDashboard
const PremiumDashboardContainer = ({
  // Dashboard data from Redux
  data,
  fetching,
  error,
  dispatch,

  // Followers
  followerState,

  // Used Coupons
  usedCouponsListState,

  // My Coupons
  myCoupons,
  viewCounts,
}) => {
  const businessInfo = useSelector((state) => state.businessInfo.data);
  const [selectedPeriod, setSelectedPeriod] = useState('LAST_THIRTY_DAYS');

  // Fetch dashboard data on mount
  useEffect(() => {
    dispatch(actions.fetchData({ period: selectedPeriod }));
    dispatch(followerActions.fetchFollowers());
    dispatch(usedCouponsListActions.fetchUsedCoupons());
    dispatch(couponsActions.fetchCoupons());
  }, [dispatch, selectedPeriod]);

  // Transform API data to component format
  const transformedData = useMemo(() => {
    return {
      businessInfo: {
        name: businessInfo?.name || 'User',
        followers: followerState?.data?.followers_count || 0,
        referrals: followerState?.data?.referrals_count || 0,
        location: businessInfo?.location || 'Your Location',
        profit: businessInfo?.profit || '0%',
        image: businessInfo?.image || 'https://images.unsplash.com/photo-1495521821757-a1efb6729352?w=400&h=400&fit=crop',
      },
      charts: {
        redemptions: data?.redemptions_chart || [],
        pointsDollars: data?.points_dollars_chart || [],
      },
      pointRequests: Array.isArray(data?.point_requests)
        ? data.point_requests.map((req) => ({
            firstName: req.first_name,
            dateTime: req.date_time,
            points: req.points,
            status: req.status,
          }))
        : [],
      usedCoupons: Array.isArray(usedCouponsListState?.data)
        ? usedCouponsListState.data.map((coupon) => ({
            id: coupon.id,
            title: coupon.title,
            expiry: coupon.expiry_date
              ? new Date(coupon.expiry_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: '2-digit',
                })
              : 'No expiry',
            favorites: coupon.save_coupon_count || 0,
            views: viewCounts?.[coupon.id] || 0,
          }))
        : [],
      actions: data?.actions || [],
    };
  }, [businessInfo, followerState, data, usedCouponsListState, viewCounts]);

  return (
    <PremiumDashboard
      dashboardData={transformedData}
      isLoading={fetching}
      error={error}
      onPeriodChange={(period) => setSelectedPeriod(period)}
    />
  );
};

// Redux mapping
const mapStateToProps = (state) => ({
  // Dashboard
  ...state.dashboards,
  // Followers
  followerState: state.followers || { data: {} },
  // Used Coupons
  usedCouponsListState: (state.usedCoupons && state.usedCoupons.list) || { data: [] },
  // Coupons
  myCoupons: state.coupons || { data: [] },
  viewCounts: (state.coupons && state.coupons.viewCounts) || {},
});

export default connect(mapStateToProps)(PremiumDashboardContainer);